import React, { useEffect, useState, useRef } from "react";
import CustomTable from "../components/customTable";
import { HiComputerDesktop } from "react-icons/hi2";
import { get, post } from "../utils/request";
import linuxIcon from "../assets/linux.png";
import windowsIcon from "../assets/windows.png";
import { format, formatDistanceToNow } from "date-fns";
import CustomLink from "../components/customLink";
import { FaEye, FaTrash } from "react-icons/fa";
import RefreshBtn from "../components/refreshBtn";
import { subscribeToPusherEvent } from "../utils/pusher";
import Loader from "../components/loader";
import { LuRefreshCw } from "react-icons/lu";
import Navbar from "../components/navbar";
import CustomAlert from "../components/customAlert";

const Devices = () => {
  let devicesArr = [];
  const [devices, setDevices] = useState([]);
  const [refreshSpins, setRefreshSpins] = useState([]);
  const [showLoader, setShowLoader] = useState(true);
  const [showRefreshAllLoader, setShowRefreshAllLoader] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const checkboxRef = useRef(null);

  const handleRefreshBtnClick = async (index, spin = true) => {
    console.log(devicesArr);

    if (!devicesArr[index]) return;

    let spins = [...refreshSpins];
    if (spin) {
      spins[index] = true;
      setRefreshSpins(spins);
    }

    const response = await post("/send-heartbeat", { uid: devicesArr[index].uid });

    if (spin) {
      spins = [...refreshSpins];
      spins[index] = false;
      setRefreshSpins(spins);
    }

    // Check for error response
    if (response && !response.success && response.error) {
      setAlertMessage(response.error);
      setShowAlert(true);
      return;
    }

    const unsubscribe = subscribeToPusherEvent("heartbeat", (data) => {
      const uid = data.uid;
      const timestamp = data.timestamp;
      devicesArr.map((device, index) => {
        if (device.uid === uid) {
          devicesArr[index].heartbeat = timestamp;
        }
      });

      setDevices(prepareRows(devicesArr));
      unsubscribe();
    });
  };

  const handleRefreshAllClick = async () => {
    setShowRefreshAllLoader(true);

    let response = await get("/");
    response = response.data;

    devicesArr = response;

    let hasError = false;
    for (const device of response) {
      const heartbeatResponse = await post("/send-heartbeat", { uid: device.uid });

      // Check for error response
      if (heartbeatResponse && !heartbeatResponse.success && heartbeatResponse.error) {
        setAlertMessage(heartbeatResponse.error);
        setShowAlert(true);
        hasError = true;
        break;
      }

      if (!hasError) {
        const unsubscribe = subscribeToPusherEvent("heartbeat", (data) => {
          const uid = data.uid;
          const timestamp = data.timestamp;
          response.map((device, index) => {
            if (device.uid === uid) {
              device.heartbeat = timestamp;
            }
          });

          setDevices(prepareRows(response));
          unsubscribe();
        });
      }
    }

    setShowRefreshAllLoader(false);
  };

  const prepareRows = (devices) => {
    const devicesArray = [];

    devices.map((row, rowIndex) => {
      const arr = [
        row.alias,
        row.hostname,
        <img
          className="w-8"
          src={row.agent === "Linux" ? linuxIcon : windowsIcon}
          alt={row.agent === "Linux" ? "Linux" : "Windows"}
        ></img>,
        <div className="flex gap-3">
          {formatDistanceToNow(new Date(row.heartbeat), {
            addSuffix: true,
          })}
          <RefreshBtn
            spin={refreshSpins[rowIndex]}
            onClick={async () => {
              await handleRefreshBtnClick(rowIndex);
            }}
          />
        </div>,
        format(new Date(row.createdAt), "dd/MM/yyyy hh:mm:ss"),
        <div className="flex gap-2 align-center">
          <CustomLink
            iconLeft={<FaEye />}
            iconSize={20}
            text={"View"}
            button={true}
            link={`/device/${row.uid}`}
          />
          <CustomLink
            iconLeft={<FaTrash />}
            text={"Delete"}
            iconSize={20}
            button={true}
            onClick={async () => {
              const response = await post("/delete-device", { uid: row.uid });
              if (response && !response.success && response.error) {
                setAlertMessage(response.error);
                setShowAlert(true);
                return;
              }
              fetchData();
            }}
            customClassName="bg-red-600 text-fg hover:bg-red-700 hover:bg-white hover:text-red-600"
          />
        </div>,
      ];
      devicesArray.push(arr);
    });
    return devicesArray;
  };

  const fetchData = async () => {
    try {
      let response = await get("/");
      response = response.data;
      devicesArr = response;

      const devicesArray = prepareRows(response);

      setDevices(devicesArray);
      setShowLoader(false);
    } catch (error) {
      console.error("Error fetching data", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [refreshSpins]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (checkboxRef.current && checkboxRef.current.checked) {
        handleRefreshAllClick();
      }
    }, 15000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  return (
    <div className="min-h-screen bg-bg">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-10">
          <h1 className="text-white text-center text-4xl md:text-5xl font-bold flex gap-4 justify-center items-center mb-2">
            <HiComputerDesktop className="text-fg" />
            Devices
            <HiComputerDesktop className="text-fg" />
          </h1>
          <p className="text-center text-gray-400 text-lg">
            Monitor and manage all your connected devices
          </p>
        </div>

        <Loader show={showLoader} className={"my-10"} text="Loading" />

        <div className={`${showLoader ? "hidden" : "block"}`}>
          <div className="mb-6 flex flex-col sm:flex-row gap-4 sm:gap-6 items-start sm:items-center bg-gray-800 p-4 rounded-xl border border-gray-700 w-fit">
            <CustomLink
              button={true}
              iconLeft={<LuRefreshCw />}
              text={"Refresh All"}
              onClick={() => {
                handleRefreshAllClick();
              }}
              showLoader={showRefreshAllLoader}
              className="w-fit flex-3"
            />
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-fg focus:ring-fg focus:ring-2"
                ref={checkboxRef}
              />
              <span className="text-gray-300 text-sm sm:text-base">
                Auto refresh every 15 seconds
              </span>
            </div>
          </div>
        </div>

        <CustomTable
          columns={[
            "Alias",
            "Hostname",
            "Agent",
            "Last Active",
            "Registered At",
            "Actions",
          ]}
          rows={devices}
        ></CustomTable>
      </div>

      <CustomAlert
        show={showAlert}
        message={alertMessage}
        onClose={() => setShowAlert(false)}
      />
    </div>
  );
};

export default Devices;
